import { FunctionComponent, ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";

import {
	faEnvelope,
	faGlasses,
	faTriangleExclamation,
	faUserPlus,
} from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { Checkbox } from "@/components/Checkbox/Checkbox";
import { LinkedinIcon } from "@/components/LinkedinIcon/LinkedinIcon";

import { CampaignNodeTypes } from "../config/node-types";
import { AddStepOptions, StepInsertionContext } from "../types/step-insertion.types";
import {
	blockForStep,
	describeInsertionPath,
	isFirstMessageWithoutConnection,
	StepPath,
	STEP_PATH_WARNING_KEYS,
} from "../utils/campaignStepPath.util";

interface SendStepOptionsProps {
	/** Absent in the per-lead editor: every option is offered as it always was. */
	insertion?: StepInsertionContext;
	edgeType?: string;
	onAdd: (type: CampaignNodeTypes, options?: AddStepOptions) => void;
}

interface OptionProps {
	title: string;
	subtitle: string;
	image: ReactElement;
	/** Why this step cannot reach the leads here — shown instead of the subtitle, option disabled. */
	block: string | null;
	/** A caveat that does not stop the step from being added. */
	warning: string | null;
	testId: string;
	onClick: () => void;
}

const SendStepOption: FunctionComponent<OptionProps> = ({
	title,
	subtitle,
	image,
	block,
	warning,
	testId,
	onClick,
}) => (
	<div>
		<Button
			disabledInGray
			className="w-full"
			color={ButtonColor.ACTION_SECONDARY}
			disabled={!!block}
			image={image}
			size={ButtonSize.ML}
			subtitle={block ?? subtitle}
			testId={testId}
			textClassName="!text-left items-start !justify-start"
			title={title}
			onClick={onClick}
		/>
		{warning && !block && (
			<p
				className="flex items-start gap-1 mt-1 text-xs text-status-warning"
				data-testid={`${testId}-warning`}
			>
				<Icon className="w-3 h-3 mt-0.5 shrink-0" icon={faTriangleExclamation} />
				{warning}
			</p>
		)}
	</div>
);

/**
 * The picker's LinkedIn and email steps. With the builder's insertion context, a step that cannot
 * reach the leads on this path is greyed out with the reason (PREC-3897): a LinkedIn message
 * below "not accepted", an email below "no email", any LinkedIn step below "no LinkedIn".
 */
export const SendStepOptions: FunctionComponent<SendStepOptionsProps> = ({
	insertion,
	edgeType,
	onAdd,
}) => {
	const { t } = useTranslation();
	const [withAcceptedCheck, setWithAcceptedCheck] = useState(true);

	const path: StepPath | null = insertion?.initiatorId
		? describeInsertionPath(insertion.initiatorId, edgeType, insertion.nodes, insertion.edges)
		: null;
	const blockText = (type: CampaignNodeTypes): string | null => {
		const block = path ? blockForStep(type, path) : null;

		return block ? t(STEP_PATH_WARNING_KEYS[block]) : null;
	};
	const firstMessageWarning =
		path && isFirstMessageWithoutConnection("linkedinNode", path)
			? t(STEP_PATH_WARNING_KEYS.FIRST_MESSAGE_NEEDS_CONNECTION)
			: null;
	const inmailWarning = insertion?.inmailSenderMissing
		? t("list.campaignBuilder.stepPath.inmailNeedsSender")
		: null;

	return (
		<>
			<div className="min-w-[calc(50%-2rem)]">
				<div className="text-sm text-text-meta mb-4 ">LinkedIn Actions</div>
				<div className="grid grid-cols-1 gap-2">
					<SendStepOption
						block={blockText("linkedinVisitNode")}
						image={<Icon className="w-4 h-4 mr-2" icon={faGlasses} />}
						subtitle=""
						testId="add-step-linkedinVisitNode"
						title="Trigger LinkedIn Profile Visit"
						warning={null}
						onClick={() => onAdd("linkedinVisitNode")}
					/>
					<div>
						<SendStepOption
							block={blockText("linkedinInviteNode")}
							image={<Icon className="w-4 h-4 mr-2" icon={faUserPlus} />}
							subtitle="(can only be sent to not-yet-connected leads)"
							testId="add-step-linkedinInviteNode"
							title="Send LinkedIn Invite"
							warning={null}
							onClick={() =>
								onAdd("linkedinInviteNode", { withAcceptedCheck: !!insertion && withAcceptedCheck })
							}
						/>
						{insertion && !blockText("linkedinInviteNode") && (
							<Checkbox
								className="mt-1.5"
								isChecked={withAcceptedCheck}
								labelClass="text-xs text-text-meta"
								name="inviteWithAcceptedCheck"
								testId="add-step-invite-accepted-check"
								onChange={() => setWithAcceptedCheck((checked) => !checked)}
							>
								{t("list.campaignBuilder.stepPath.inviteWithAcceptedCheck")}
							</Checkbox>
						)}
					</div>
					<SendStepOption
						block={blockText("linkedinNode")}
						image={<LinkedinIcon />}
						subtitle="(can only be sent to connected leads)"
						testId="add-step-linkedinNode"
						title="Send LinkedIn Message"
						warning={firstMessageWarning}
						onClick={() => onAdd("linkedinNode")}
					/>
					<SendStepOption
						block={blockText("linkedinInmailNode")}
						image={<LinkedinIcon />}
						subtitle="(requires LinkedIn Sales Navigator or Premium)"
						testId="add-step-linkedinInmailNode"
						title="Send LinkedIn InMail"
						warning={inmailWarning}
						onClick={() => onAdd("linkedinInmailNode")}
					/>
				</div>
			</div>
			<div className="min-w-[calc(50%-2rem)] row-start-3 self-end">
				<div className="text-sm text-text-meta mb-4">Email Actions</div>
				<div className="grid grid-cols-1 gap-2">
					<SendStepOption
						block={blockText("emailNode")}
						image={<Icon className="w-4 h-4 mr-2" icon={faEnvelope} />}
						subtitle="(requires enriched email)"
						testId="add-step-emailNode"
						title="Send Email Message"
						warning={null}
						onClick={() => onAdd("emailNode")}
					/>
				</div>
			</div>
		</>
	);
};
